"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import Anchor from "@shared-ui/components/Anchor"
import { usePathname } from "next/navigation"

interface InfluencerSearchWidgetProps {
    position?: "left" | "right"
    showOnPaths?: string[]
}

const STORAGE_KEY = "influencer-widget-dismissed"
const PANEL_ID = "influencer-search-panel"
const DEFAULT_SHOW_ON_PATHS = ["/free-instagram-tools", "/instagram-", "/blog"]

export const InfluencerSearchWidget: React.FC<InfluencerSearchWidgetProps> = ({ position = "right", showOnPaths = DEFAULT_SHOW_ON_PATHS }) => {
    const [openPathname, setOpenPathname] = useState<string | null>(null)
    const [hasLegacyDismissal, setHasLegacyDismissal] = useState(true) // Start hidden to prevent a flash
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const launcherButtonRef = useRef<HTMLButtonElement>(null)
    const shouldReturnFocusRef = useRef(false)
    const pathname = usePathname()
    const isInstagramToolRoute = pathname?.includes("/free-instagram-tools")
    const isEligible =
        !isInstagramToolRoute &&
        showOnPaths.some((path) => pathname?.includes(path)) &&
        !pathname?.includes("/find-influencers")
    const isOpen = !hasLegacyDismissal && isEligible && openPathname === pathname
    const widgetState: "hidden" | "closed" | "open" = hasLegacyDismissal || !isEligible ? "hidden" : isOpen ? "open" : "closed"

    // Preserve the legacy dismissal as a full hidden-state veto.
    useEffect(() => {
        const dismissed = localStorage.getItem(STORAGE_KEY)
        setHasLegacyDismissal(dismissed === "true")
    }, [])

    // Close immediately on navigation, then clear the old route binding.
    useEffect(() => {
        if (openPathname !== null && openPathname !== pathname) {
            setOpenPathname(null)
        }
    }, [openPathname, pathname])

    const closeWidget = useCallback(() => {
        shouldReturnFocusRef.current = true
        setOpenPathname(null)
    }, [])

    useEffect(() => {
        if (isOpen) {
            closeButtonRef.current?.focus()

            const handleKeyDown = (event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    event.preventDefault()
                    closeWidget()
                }
            }

            document.addEventListener("keydown", handleKeyDown)
            return () => document.removeEventListener("keydown", handleKeyDown)
        }

        if (shouldReturnFocusRef.current) {
            shouldReturnFocusRef.current = false
            launcherButtonRef.current?.focus()
        }
    }, [closeWidget, isOpen])

    const openWidget = () => {
        shouldReturnFocusRef.current = false
        setOpenPathname(pathname)
    }

    if (widgetState === "hidden") return null

    return (
        <div className={`fixed ${position === "left" ? "left-4" : "right-4"} bottom-20 z-40 ${widgetState === "open" ? "w-[280px]" : "w-14"}`}>
            {widgetState === "open" ? (
                <div
                    id={PANEL_ID}
                    role="dialog"
                    aria-labelledby={`${PANEL_ID}-title`}
                    aria-describedby={`${PANEL_ID}-description`}
                    className="overflow-hidden rounded-card bg-ink shadow-card-hover">
                    {/* Header */}
                    <div className="p-4 pb-3">
                        <div className="flex items-center justify-between">
                            <h3 id={`${PANEL_ID}-title`} className="text-[14px] font-semibold text-white">
                                Find Influencers
                            </h3>
                            <button
                                ref={closeButtonRef}
                                type="button"
                                onClick={closeWidget}
                                className="-m-3 flex h-11 w-11 items-center justify-center text-white/50 transition-colors hover:text-white"
                                aria-label="Close Find Influencers widget">
                                <svg aria-hidden="true" focusable="false" className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p id={`${PANEL_ID}-description`} className="mt-0.5 text-[12px] text-white/60">
                            300M+ verified creators worldwide
                        </p>
                    </div>

                    {/* Content */}
                    <div className="space-y-4 px-4 pb-4">
                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="bg-white/8 rounded-badge p-2.5 text-center">
                                <div className="text-[16px] font-bold text-white">195+</div>
                                <div className="text-[11px] text-white/60">Countries</div>
                            </div>
                            <div className="bg-white/8 rounded-badge p-2.5 text-center">
                                <div className="text-[16px] font-bold text-white">50+</div>
                                <div className="text-[11px] text-white/60">Niches</div>
                            </div>
                            <div className="bg-white/8 rounded-badge p-2.5 text-center">
                                <div className="text-[16px] font-bold text-pink">300M+</div>
                                <div className="text-[11px] text-white/60">Profiles</div>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="space-y-2">
                            <Anchor href="/find-influencers" className="flex w-full items-center justify-center gap-2 rounded-button bg-pink px-4 py-2.5 text-[14px] font-semibold text-ink transition-colors hover:bg-pink-dark active:scale-[0.98]">
                                <svg aria-hidden="true" focusable="false" className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                Browse All Countries
                            </Anchor>
                        </div>

                        {/* Popular Searches */}
                        <div className="border-t border-white/10 pt-3">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-white/50">Popular</p>
                            <div className="flex flex-wrap gap-1.5">
                                {["USA", "Fashion", "Micro", "Beauty"].map((term) => (
                                    <Anchor
                                        key={term}
                                        href={`/find-influencers${term === "USA" ? "/united-states" : `/top-${term.toLowerCase()}-influencers`}`}
                                        className="bg-white/8 inline-block rounded-pill px-2.5 py-1 text-[12px] text-white/70 transition-colors hover:bg-white/15 hover:text-white">
                                        {term}
                                    </Anchor>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <button
                    ref={launcherButtonRef}
                    type="button"
                    onClick={openWidget}
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-pink shadow-card-hover active:scale-95"
                    aria-label="Open Find Influencers widget"
                    aria-controls={PANEL_ID}
                    aria-expanded={isOpen}
                    aria-haspopup="dialog">
                    <svg aria-hidden="true" focusable="false" className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </button>
            )}
        </div>
    )
}
